import React, { useRef, useState } from 'react';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import {
    formatFileSizeError,
    MAX_UPLOAD_BYTES,
    resolveAttachmentUrl,
} from '../../utils/attachmentUrl';
import { uploadTaskFile } from '../../api/backendClient';
import { fireErrorToast } from '../../utils/commonFunction';

const ProfileImageField = ({
    label = 'Profile photo',
    value = '',
    onChange,
    walletAddress = '',
    backendToken = '',
    disabled = false,
    hint = 'Optional. JPG, PNG, or WebP up to 10 MB.',
}) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const imageUrl = value ? resolveAttachmentUrl(value) : '';

    const handleFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || disabled) return;

        if (!file.type.startsWith('image/')) {
            fireErrorToast('Please choose an image file.');
            return;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            fireErrorToast(formatFileSizeError());
            return;
        }
        if (!backendToken) {
            fireErrorToast('Connect your wallet session before uploading.');
            return;
        }

        try {
            setUploading(true);
            const data = await uploadTaskFile(backendToken, file);
            if (!data?.url) throw new Error('Upload failed');
            onChange?.(data.url);
        } catch (error) {
            fireErrorToast(
                error?.response?.data?.message ||
                    error?.message ||
                    'Could not upload image',
            );
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="space-y-2">
            <span className="block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                {label}
            </span>
            <div className="flex flex-wrap items-center gap-4">
                <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-100 ring-1 ring-zinc-200 dark:border-zinc-700 dark:bg-zinc-900 dark:ring-zinc-700">
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt=""
                            className="h-full w-full object-cover"
                        />
                    ) : walletAddress ? (
                        <Jazzicon
                            diameter={80}
                            seed={jsNumberForAddress(walletAddress)}
                        />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-zinc-400">
                            <i className="fa-regular fa-image text-xl" />
                        </div>
                    )}
                    {uploading ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white">
                            <i className="fa-solid fa-spinner fa-spin text-sm" />
                        </div>
                    ) : null}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        onChange={handleFile}
                        disabled={disabled || uploading}
                    />
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            disabled={disabled || uploading}
                            onClick={() => inputRef.current?.click()}
                            className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-800 transition hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                        >
                            {uploading ? 'Uploading…' : 'Upload photo'}
                        </button>
                        {value ? (
                            <button
                                type="button"
                                disabled={disabled || uploading}
                                onClick={() => onChange?.('')}
                                className="rounded-lg border border-zinc-200 px-3 py-2 text-xs font-semibold text-zinc-600 transition hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
                            >
                                Remove
                            </button>
                        ) : null}
                    </div>
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-500">{hint}</p>
                </div>
            </div>
        </div>
    );
};

export default ProfileImageField;
